import type { LyricTimingLine } from 'fym-shared/src/music24/lyricTiming';

/** The text of a line with no time (design s8: grey `—:—`). */
export const LYRIC_NO_TIME = '—:—';

/** ▶ plays the master from this much before the start of the line (design s8). */
export const PLAY_LEAD_IN_SEC = 0.5;

/** − and + move a start by this much; with Shift by `NUDGE_SHIFT_MS` (design s8). */
export const NUDGE_MS = 100;
export const NUDGE_SHIFT_MS = 500;

/** The time of a line start as `m:ss.s` (`1:04.3`), as the design shows it. */
export function formatLineTime(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) {
    return LYRIC_NO_TIME;
  }
  const tenths = Math.round(ms / 100);
  const minutes = Math.floor(tenths / 600);
  const rest = (tenths - minutes * 600) / 10;
  return `${minutes}:${rest < 10 ? '0' : ''}${rest.toFixed(1)}`;
}

/** Where ▶ starts the master for a line: 0.5 s before its start, never before 0. Null for no time. */
export function playStartSec(line: LyricTimingLine): number | null {
  if (line.startMs === null) {
    return null;
  }
  return Math.max(0, line.startMs / 1000 - PLAY_LEAD_IN_SEC);
}

export type RelativeUnit = 'second' | 'minute' | 'hour' | 'day';

/**
 * How long ago an ISO time was, for `Intl.RelativeTimeFormat` ("2 min ago"): a negative value and
 * a unit. Null for a missing or broken time.
 */
export function agoParts(iso: string | null, nowMs: number): { value: number; unit: RelativeUnit } | null {
  if (iso === null) {
    return null;
  }
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) {
    return null;
  }
  const seconds = Math.max(0, Math.round((nowMs - at) / 1000));
  if (seconds < 60) {
    return { value: -seconds, unit: 'second' };
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return { value: -minutes, unit: 'minute' };
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return { value: -hours, unit: 'hour' };
  }
  return { value: -Math.floor(hours / 24), unit: 'day' };
}
